import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CalendarDays,
  Edit3,
  Filter,
  Flame,
  Medal,
  Plus,
  RefreshCcw,
  Search,
  Sparkles,
  Swords,
  Target,
  Trophy,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';

import { equipment, matches } from '../../lib/api';
import { markMatchDataUpdated } from '../../lib/dataFreshness';
import { Button, Field, Input, Select, Textarea } from '../../components/ui-kit';

const DEFAULT_PLAYER_STATS = Object.freeze({
  aces: 0,
  double_faults: 0,
  winners: 0,
  unforced_errors: 0,
});

const SURFACES = ['hard', 'clay', 'grass', 'carpet'];
const MATCH_TYPES = ['singles', 'doubles'];
const RESULTS = ['win', 'loss'];

const emptyMatchDraft = () => ({
  opponent_name: '',
  date: new Date().toISOString().split('T')[0],
  surface: 'hard',
  match_type: 'singles',
  result: 'win',
  score: '',
  sets: [],
  notes: '',
  player_stats: { ...DEFAULT_PLAYER_STATS },
  equipment_ids: [],
});

const localeFor = (lang) =>
  lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-GB';

const EMPTY_DISPLAY_VALUE = '-';

const safeDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value, locale) => {
  const parsed = safeDate(value);
  return parsed
    ? parsed.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
    : EMPTY_DISPLAY_VALUE;
};

const toDateInputValue = (value) => {
  const parsed = safeDate(value);
  return parsed ? parsed.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
};

const normalizeSets = (sets = []) =>
  sets.map((set) => ({
    player_games: String(set.player_games ?? ''),
    opponent_games: String(set.opponent_games ?? ''),
    player_tiebreak: set.player_tiebreak ?? '',
    opponent_tiebreak: set.opponent_tiebreak ?? '',
  }));

const buildSetsPayload = (sets = []) =>
  sets
    .filter((set) => String(set.player_games).trim() !== '' || String(set.opponent_games).trim() !== '')
    .map((set, index) => ({
      set_number: index + 1,
      player_games: Number.parseInt(set.player_games, 10) || 0,
      opponent_games: Number.parseInt(set.opponent_games, 10) || 0,
      ...(String(set.player_tiebreak).trim() !== ''
        ? { player_tiebreak: Number.parseInt(set.player_tiebreak, 10) || 0 }
        : {}),
      ...(String(set.opponent_tiebreak).trim() !== ''
        ? { opponent_tiebreak: Number.parseInt(set.opponent_tiebreak, 10) || 0 }
        : {}),
    }));

const computeCurrentStreak = (rows, t) => {
  let streak = 0;
  let lastResult = null;
  for (const match of rows) {
    if (!match.result) continue;
    if (!lastResult) {
      lastResult = match.result;
      streak = 1;
      continue;
    }
    if (match.result === lastResult) {
      streak += 1;
    } else {
      break;
    }
  }
  if (!lastResult) return EMPTY_DISPLAY_VALUE;
  return t(lastResult === 'win' ? 'match.streakValueWin' : 'match.streakValueLoss', {
    count: streak,
    defaultValue: lastResult === 'win' ? '{{count}} win' : '{{count}} loss',
  });
};

const computeBestSurface = (stats) => {
  const entries = Object.entries(stats?.surface_stats || {});
  if (entries.length === 0) return EMPTY_DISPLAY_VALUE;
  const [surface] = entries.sort(([, a], [, b]) => {
    const aTotal = (a.wins || 0) + (a.losses || 0);
    const bTotal = (b.wins || 0) + (b.losses || 0);
    const aRate = aTotal ? (a.wins || 0) / aTotal : 0;
    const bRate = bTotal ? (b.wins || 0) / bTotal : 0;
    return bRate - aRate || bTotal - aTotal;
  })[0];
  return surface;
};

const getCurrentStreakResult = (rows) =>
  rows.find((match) => match.result)?.result || null;

const ResultBadge = ({ result, t }) => {
  const win = result === 'win';
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-black ring-1 ${
        win
          ? 'bg-lime-50 text-lime-700 ring-lime-100'
          : 'bg-rose-50 text-rose-700 ring-rose-100'
      }`}
      data-testid="match-result-badge"
    >
      {win ? t('match.win', 'Win') : t('match.loss', 'Loss')}
    </span>
  );
};

const SurfaceBadge = ({ surface, t }) => (
  <span className="inline-flex items-center rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-700 ring-1 ring-zinc-200">
    {t(`court.surfaces.${surface}`, surface || EMPTY_DISPLAY_VALUE)}
  </span>
);

const MatchTypeBadge = ({ type, t }) => (
  <span className="inline-flex items-center rounded-full bg-zinc-950 px-3 py-1 text-xs font-black text-lime-300">
    {t(`match.types.${type}`, type || EMPTY_DISPLAY_VALUE)}
  </span>
);

const equipmentLabel = (item, t) =>
  item?.category
    ? `${item.name} (${t(`equipment.categories.${item.category}`, item.category)})`
    : item?.name;

const selectedEquipmentIdsFromEvent = (event) =>
  Array.from(event.target.selectedOptions).map((option) => option.value);

const LinkedEquipmentList = ({ equipmentIds = [], equipmentById, t }) => {
  if (!Array.isArray(equipmentIds) || equipmentIds.length === 0) return null;
  const labels = equipmentIds.map((id) => equipmentById.get(id)?.name || id);

  return (
    <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
      <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
        {t('equipment.equipmentUsed')}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {labels.map((label) => (
          <span key={label} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-zinc-700 ring-1 ring-zinc-200">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const EquipmentSelector = ({ equipmentList, selectedIds, onChange, t }) => (
  <Field
    label={t('equipment.linkedEquipment')}
    hint={equipmentList.length > 0 ? t('equipment.selectEquipment') : t('equipment.addEquipmentFirst')}
  >
    {equipmentList.length > 0 ? (
      <Select
        multiple
        className="min-h-32"
        value={selectedIds || []}
        onChange={(event) => onChange(selectedEquipmentIdsFromEvent(event))}
      >
        {equipmentList.map((item) => (
          <option key={item.id} value={item.id}>
            {equipmentLabel(item, t)}
          </option>
        ))}
      </Select>
    ) : (
      <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3 text-sm text-zinc-500">
        {t('equipment.noEquipmentAddedYet')} {t('equipment.addEquipmentFirst')}.
      </p>
    )}
  </Field>
);

const StatTile = ({ icon, label, value, detail, tone = 'lime' }) => {
  const toneClass =
    tone === 'rose'
      ? 'bg-rose-50 text-rose-700 ring-rose-100'
      : tone === 'zinc'
      ? 'bg-zinc-100 text-zinc-700 ring-zinc-200'
      : 'bg-lime-50 text-lime-700 ring-lime-100';
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-zinc-500">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${toneClass}`}>
          {icon}
        </div>
      </div>
      {detail ? <p className="mt-3 text-sm text-zinc-500">{detail}</p> : null}
    </div>
  );
};

const PlayerStatBlock = ({ label, value }) => (
  <div className="rounded-lg bg-zinc-50 p-3">
    <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{label}</p>
    <p className="mt-1 text-lg font-black text-zinc-950">{value ?? 0}</p>
  </div>
);

const MatchSkeleton = () => (
  <div className="grid gap-4 lg:grid-cols-2">
    {[0, 1, 2, 3].map((item) => (
      <div key={item} className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="h-4 w-28 animate-pulse rounded bg-zinc-200" />
        <div className="mt-4 h-8 w-48 animate-pulse rounded bg-zinc-200" />
        <div className="mt-5 h-24 animate-pulse rounded bg-zinc-100" />
      </div>
    ))}
  </div>
);

const EmptyMatchState = ({ onAdd, t }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center" data-testid="matches-empty">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      <Trophy size={26} strokeWidth={1.8} />
    </div>
    <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
      {t('match.noMatches', 'No matches yet')}
    </h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
      {t('match.emptyBody', 'Log your first match to start tracking results, scores, and patterns.')}
    </p>
    <button
      type="button"
      onClick={onAdd}
      className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 hover:bg-lime-200"
    >
      <Plus size={16} strokeWidth={1.8} />
      {t('match.addMatch', 'Add match')}
    </button>
  </div>
);

const ErrorState = ({ onRetry, t }) => (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-amber-950">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="font-black">{t('match.loadError', 'Could not load matches')}</h2>
        <p className="mt-1 text-sm text-amber-800">
          {t('match.loadErrorBody', 'Your match diary could not be refreshed.')}
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 text-sm font-black text-amber-950"
      >
        <RefreshCcw size={16} strokeWidth={1.8} />
        {t('common.retry', 'Retry')}
      </button>
    </div>
  </div>
);

const MatchCard = ({ match, t, locale, onEdit, onDelete, equipmentById }) => {
  const stats = match.player_stats || {};
  return (
    <article
      className={`rounded-lg border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        match.result === 'win' ? 'border-lime-200' : 'border-rose-200'
      }`}
      data-testid={`match-card-${match.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
            {formatDate(match.date, locale)}
          </p>
          <h3 className="mt-2 text-xl font-black tracking-normal text-zinc-950">
            {t('match.vsOpponent', { name: match.opponent_name, defaultValue: `vs ${match.opponent_name}` })}
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <ResultBadge result={match.result} t={t} />
          <SurfaceBadge surface={match.surface} t={t} />
          <MatchTypeBadge type={match.match_type} t={t} />
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
        <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
          {t('match.score', 'Score')}
        </p>
        <p className="mt-1 text-3xl font-black tracking-normal text-zinc-950">
          {match.score}
        </p>
      </div>

      {Array.isArray(match.sets) && match.sets.length > 0 ? (
        <div className="mt-4 grid gap-2">
          {match.sets.map((set) => (
            <div key={set.set_number} className="flex min-h-11 items-center justify-between rounded-lg bg-white px-3 ring-1 ring-zinc-200">
              <span className="text-sm font-bold text-zinc-500">
                {t('match.setLabel', 'Set')} {set.set_number}
              </span>
              <span className="font-black text-zinc-950">
                {set.player_games}-{set.opponent_games}
                {set.player_tiebreak != null || set.opponent_tiebreak != null
                  ? ` (${set.player_tiebreak || 0}-${set.opponent_tiebreak || 0})`
                  : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {match.player_stats ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <PlayerStatBlock label={t('match.aces', 'Aces')} value={stats.aces} />
          <PlayerStatBlock label={t('match.doubleFaults', 'Double faults')} value={stats.double_faults} />
          <PlayerStatBlock label={t('match.winners', 'Winners')} value={stats.winners} />
          <PlayerStatBlock label={t('match.unforcedErrors', 'Unforced errors')} value={stats.unforced_errors} />
        </div>
      ) : null}

      <LinkedEquipmentList
        equipmentIds={match.equipment_ids}
        equipmentById={equipmentById}
        t={t}
      />

      {match.notes ? (
        <p className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm leading-6 text-zinc-600">
          {match.notes}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => onEdit(match)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-black text-zinc-950 hover:bg-zinc-50"
        >
          <Edit3 size={15} strokeWidth={1.8} />
          {t('common.edit', 'Edit')}
        </button>
        <button
          type="button"
          onClick={() => onDelete(match.id)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 text-sm font-black text-rose-700 hover:bg-rose-100"
          data-testid={`match-delete-${match.id}`}
        >
          <Trash2 size={15} strokeWidth={1.8} />
          {t('common.delete', 'Delete')}
        </button>
      </div>
    </article>
  );
};

const MatchForm = ({ mode, draft, setDraft, equipmentList, onClose, onSubmit, submitting, t }) => {
  const addSet = () =>
    setDraft({
      ...draft,
      sets: [...draft.sets, { player_games: '', opponent_games: '', player_tiebreak: '', opponent_tiebreak: '' }],
    });

  const updateSet = (index, field, value) => {
    const next = [...draft.sets];
    next[index] = { ...next[index], [field]: value };
    setDraft({ ...draft, sets: next });
  };

  const removeSet = (index) => {
    const next = draft.sets.filter((_, idx) => idx !== index);
    setDraft({ ...draft, sets: next });
  };

  const updateStat = (field, raw) =>
    setDraft({
      ...draft,
      player_stats: {
        ...draft.player_stats,
        [field]: Number.parseInt(raw, 10) || 0,
      },
    });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-950/70 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="match-modal"
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-zinc-200 bg-zinc-950 p-5 text-white">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
              {t('match.formKicker', 'Match form')}
            </p>
            <h2 className="mt-1 text-2xl font-black tracking-normal text-white">
              {mode === 'edit' ? t('match.editMatch', 'Edit match') : t('match.newMatch', 'New match')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white"
            aria-label={t('common.cancel', 'Cancel')}
            data-testid="match-modal-close"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="grid max-h-[78vh] gap-4 overflow-y-auto border-0 bg-white p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('match.opponent', 'Opponent')} required>
              <Input
                type="text"
                data-testid="match-input-opponent"
                value={draft.opponent_name}
                onChange={(event) => setDraft({ ...draft, opponent_name: event.target.value })}
                required
              />
            </Field>
            <Field label={t('match.date', 'Date')} required>
              <Input
                type="date"
                data-testid="match-input-date"
                value={draft.date}
                onChange={(event) => setDraft({ ...draft, date: event.target.value })}
                required
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t('match.surface', 'Surface')}>
              <Select
                data-testid="match-input-surface"
                value={draft.surface}
                onChange={(event) => setDraft({ ...draft, surface: event.target.value })}
              >
                {SURFACES.map((surface) => (
                  <option key={surface} value={surface}>
                    {t(`court.surfaces.${surface}`, surface)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('match.matchType', 'Match type')}>
              <Select
                data-testid="match-input-type"
                value={draft.match_type}
                onChange={(event) => setDraft({ ...draft, match_type: event.target.value })}
              >
                {MATCH_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`match.types.${type}`, type)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('match.result', 'Result')}>
              <Select
                data-testid="match-input-result"
                value={draft.result}
                onChange={(event) => setDraft({ ...draft, result: event.target.value })}
              >
                {RESULTS.map((result) => (
                  <option key={result} value={result}>
                    {result === 'win' ? t('match.win', 'Win') : t('match.loss', 'Loss')}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label={t('match.score', 'Score')} required hint={t('match.scoreHint', 'Example: 6-4, 3-6, 6-2')}>
            <Input
              type="text"
              data-testid="match-input-score"
              value={draft.score}
              onChange={(event) => setDraft({ ...draft, score: event.target.value })}
              placeholder="6-4, 3-6, 6-2"
              required
            />
          </Field>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black text-zinc-950">{t('match.sets', 'Sets')}</p>
                <p className="text-sm text-zinc-500">{t('match.setsHint', 'Optional set-by-set score rows.')}</p>
              </div>
              <button
                type="button"
                onClick={addSet}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-3 text-sm font-black text-zinc-950"
                data-testid="match-add-set"
              >
                <Plus size={15} strokeWidth={1.8} />
                {t('match.addSet', 'Add set')}
              </button>
            </div>
            <div className="mt-4 grid gap-3">
              {draft.sets.map((set, index) => (
                <div key={index} className="grid gap-2 rounded-lg bg-white p-3 ring-1 ring-zinc-200 sm:grid-cols-[5rem_1fr_1fr_1fr_1fr_2.75rem] sm:items-end">
                  <span className="text-sm font-black text-zinc-500">
                    {t('match.setLabel', 'Set')} {index + 1}
                  </span>
                  <Input
                    type="number"
                    value={set.player_games}
                    onChange={(event) => updateSet(index, 'player_games', event.target.value)}
                    placeholder={t('match.you', 'You')}
                    min="0"
                  />
                  <Input
                    type="number"
                    value={set.opponent_games}
                    onChange={(event) => updateSet(index, 'opponent_games', event.target.value)}
                    placeholder={t('match.them', 'Them')}
                    min="0"
                  />
                  <Input
                    type="number"
                    value={set.player_tiebreak}
                    onChange={(event) => updateSet(index, 'player_tiebreak', event.target.value)}
                    placeholder={t('match.tbYou', 'TB you')}
                    min="0"
                  />
                  <Input
                    type="number"
                    value={set.opponent_tiebreak}
                    onChange={(event) => updateSet(index, 'opponent_tiebreak', event.target.value)}
                    placeholder={t('match.tbThem', 'TB them')}
                    min="0"
                  />
                  <button
                    type="button"
                    onClick={() => removeSet(index)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-700"
                    aria-label={t('common.delete', 'Delete')}
                  >
                    <Trash2 size={15} strokeWidth={1.8} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <p className="mb-3 text-sm font-black text-zinc-950">{t('match.stats', 'Statistics')}</p>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field label={t('match.aces', 'Aces')}>
                <Input type="number" data-testid="match-stat-aces" value={draft.player_stats.aces} onChange={(event) => updateStat('aces', event.target.value)} min="0" />
              </Field>
              <Field label={t('match.doubleFaults', 'Double faults')}>
                <Input type="number" data-testid="match-stat-double-faults" value={draft.player_stats.double_faults} onChange={(event) => updateStat('double_faults', event.target.value)} min="0" />
              </Field>
              <Field label={t('match.winners', 'Winners')}>
                <Input type="number" data-testid="match-stat-winners" value={draft.player_stats.winners} onChange={(event) => updateStat('winners', event.target.value)} min="0" />
              </Field>
              <Field label={t('match.unforcedErrors', 'Unforced errors')}>
                <Input type="number" data-testid="match-stat-unforced-errors" value={draft.player_stats.unforced_errors} onChange={(event) => updateStat('unforced_errors', event.target.value)} min="0" />
              </Field>
            </div>
          </div>

          <Field label={t('match.notes', 'Notes')}>
            <Textarea
              rows={3}
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            />
          </Field>

          <EquipmentSelector
            equipmentList={equipmentList}
            selectedIds={draft.equipment_ids}
            onChange={(equipmentIds) => setDraft({ ...draft, equipment_ids: equipmentIds })}
            t={t}
          />

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" size="lg" type="button" onClick={onClose} data-testid="match-modal-cancel">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="primary" size="lg" type="submit" loading={submitting} data-testid="match-modal-save">
              {mode === 'edit' ? t('common.save', 'Save') : t('match.addMatch', 'Add match')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MatchesPage = () => {
  const { t, i18n } = useTranslation();
  const [matchesList, setMatchesList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [formMode, setFormMode] = useState(null);
  const [activeMatch, setActiveMatch] = useState(null);
  const [draft, setDraft] = useState(emptyMatchDraft);
  const [submitting, setSubmitting] = useState(false);

  const locale = useMemo(() => localeFor(i18n.language), [i18n.language]);
  const equipmentById = useMemo(
    () => new Map(equipmentList.map((item) => [item.id, item])),
    [equipmentList],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [matchesRes, statsRes, equipmentRes] = await Promise.all([
        matches.getAll(),
        matches.getStats(),
        equipment.getAll().catch(() => ({ data: [] })),
      ]);
      setMatchesList(matchesRes.data);
      setStats(statsRes.data);
      setEquipmentList(Array.isArray(equipmentRes.data) ? equipmentRes.data : []);
    } catch (error) {
      toast.error(t('match.loadError', 'Could not load matches'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const bestSurface = useMemo(() => computeBestSurface(stats), [stats]);
  const streak = useMemo(() => computeCurrentStreak(matchesList, t), [matchesList, t]);
  const streakResult = useMemo(() => getCurrentStreakResult(matchesList), [matchesList]);

  const filteredMatches = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return matchesList.filter((match) => {
      const matchesResult = filter === 'all' || match.result === filter;
      const haystack = [
        match.opponent_name,
        match.score,
        match.surface,
        match.match_type,
        match.notes,
        formatDate(match.date, locale),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesQuery = !normalizedQuery || haystack.includes(normalizedQuery);
      return matchesResult && matchesQuery;
    });
  }, [matchesList, filter, query, locale]);

  const openCreate = () => {
    setActiveMatch(null);
    setDraft(emptyMatchDraft());
    setFormMode('create');
  };

  const openEdit = (match) => {
    setActiveMatch(match);
    setDraft({
      opponent_name: match.opponent_name || '',
      date: toDateInputValue(match.date),
      surface: match.surface || 'hard',
      match_type: match.match_type || 'singles',
      result: match.result || 'win',
      score: match.score || '',
      sets: normalizeSets(match.sets),
      notes: match.notes || '',
      equipment_ids: Array.isArray(match.equipment_ids) ? match.equipment_ids : [],
      player_stats: {
        ...DEFAULT_PLAYER_STATS,
        ...(match.player_stats || {}),
      },
    });
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode(null);
    setActiveMatch(null);
    setDraft(emptyMatchDraft());
  };

  const buildPayload = () => ({
    opponent_name: draft.opponent_name.trim(),
    date: new Date(draft.date).toISOString(),
    surface: draft.surface,
    match_type: draft.match_type,
    result: draft.result,
    score: draft.score.trim(),
    sets: buildSetsPayload(draft.sets),
    player_stats: {
      aces: Number(draft.player_stats.aces) || 0,
      double_faults: Number(draft.player_stats.double_faults) || 0,
      winners: Number(draft.player_stats.winners) || 0,
      unforced_errors: Number(draft.player_stats.unforced_errors) || 0,
    },
    notes: draft.notes.trim() || null,
    equipment_ids: Array.isArray(draft.equipment_ids) ? draft.equipment_ids : [],
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (formMode === 'edit' && activeMatch?.id) {
        await matches.update(activeMatch.id, payload);
        toast.success(t('match.updateSuccess', 'Match updated.'));
      } else {
        await matches.create(payload);
        toast.success(t('match.createSuccess', 'Match added.'));
      }
      closeForm();
      markMatchDataUpdated();
      loadData();
    } catch (error) {
      toast.error(formMode === 'edit' ? t('match.updateError', 'Could not update match.') : t('match.createError', 'Could not add match.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMatch = async (id) => {
    if (!window.confirm(t('match.deleteConfirm', 'Delete this match?'))) return;
    try {
      await matches.delete(id);
      toast.success(t('match.deleteSuccess', 'Match deleted.'));
      markMatchDataUpdated();
      loadData();
    } catch (error) {
      toast.error(t('match.deleteError', 'Could not delete match.'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-zinc-100 p-4 lg:p-8" data-testid="matches-loading">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 h-52 animate-pulse rounded-lg bg-zinc-200" />
          <MatchSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 px-4 py-5 text-zinc-950 lg:px-8 lg:py-8" data-testid="matches-page">
      <div className="mx-auto max-w-7xl">
        <section className="relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 p-5 text-white shadow-xl md:p-7">
          <div
            className="absolute inset-0 bg-[linear-gradient(90deg,rgba(183,255,42,0.18)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.09)_1px,transparent_1px)] bg-[size:72px_72px] opacity-45"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
                <Sparkles size={14} strokeWidth={1.8} />
                {t('match.heroKicker', 'Match Diary')}
              </div>
              <h1 className="mt-5 text-4xl font-black tracking-normal text-white md:text-5xl">
                {t('match.title', 'Matches')}
              </h1>
              <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-200">
                {t('match.subtitle', 'Track your matches and progress')}
              </p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 hover:bg-lime-200"
              data-testid="match-add-button"
            >
              <Plus size={18} strokeWidth={1.8} />
              {t('match.addMatch', 'Add match')}
            </button>
          </div>
        </section>

        {loadFailed ? <div className="mt-5"><ErrorState onRetry={loadData} t={t} /></div> : null}

        <section className="mt-5 grid gap-4 md:grid-cols-4">
          <StatTile
            icon={<Trophy size={22} strokeWidth={1.8} />}
            label={t('match.totalMatches', 'Total matches')}
            value={stats?.total_matches ?? 0}
            detail={t('match.totalMatchesDetail', 'Recorded matches')}
          />
          <StatTile
            icon={<TrendingUp size={22} strokeWidth={1.8} />}
            label={t('match.winPercentage', 'Win %')}
            value={`${stats?.win_percentage ?? 0}%`}
            detail={`${stats?.wins ?? 0} ${t('match.winsCount', 'Wins')} / ${stats?.losses ?? 0} ${t('match.lossesCount', 'Losses')}`}
          />
          <StatTile
            icon={<Flame size={22} strokeWidth={1.8} />}
            label={t('match.currentStreak', 'Current streak')}
            value={streak}
            detail={t('match.streakDetail', 'Latest result run')}
            tone={streakResult === 'loss' ? 'rose' : 'lime'}
          />
          <StatTile
            icon={<Medal size={22} strokeWidth={1.8} />}
            label={t('match.bestSurface', 'Best surface')}
            value={bestSurface === EMPTY_DISPLAY_VALUE ? EMPTY_DISPLAY_VALUE : t(`court.surfaces.${bestSurface}`, bestSurface)}
            detail={t('match.bestSurfaceDetail', 'By win rate')}
            tone="zinc"
          />
        </section>

        <section className="mt-5 grid gap-4 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} strokeWidth={1.8} />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('match.searchPlaceholder', 'Search opponent, score, surface...')}
              className="min-h-11 w-full rounded-lg border border-zinc-200 bg-zinc-50 py-2 pl-10 pr-3 text-sm text-zinc-950 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-200/50"
            />
          </div>
          <div className="grid grid-cols-3 gap-2" role="tablist" aria-label={t('match.filterAriaLabel', 'Match result filter')}>
            {RESULTS.concat('all').reverse().map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setFilter(tab)}
                className={`min-h-11 rounded-lg px-3 text-sm font-black transition ${
                  filter === tab
                    ? 'bg-zinc-950 text-lime-300'
                    : 'border border-zinc-200 bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
                }`}
                data-testid={`match-filter-${tab}`}
                role="tab"
                aria-selected={filter === tab}
              >
                {tab === 'all' ? t('match.allMatches', 'All') : tab === 'win' ? t('match.winsCount', 'Wins') : t('match.lossesCount', 'Losses')}
              </button>
            ))}
          </div>
        </section>

        <section className="mt-5">
          {matchesList.length === 0 ? (
            <EmptyMatchState onAdd={openCreate} t={t} />
          ) : filteredMatches.length === 0 ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center">
              <Target className="mx-auto text-zinc-400" size={32} strokeWidth={1.7} />
              <h2 className="mt-3 text-xl font-black text-zinc-950">
                {t('match.noFilteredResults', 'No matches match your filters')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setFilter('all');
                }}
                className="mt-4 inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 px-4 text-sm font-black text-zinc-950"
              >
                {t('match.clearFilters', 'Clear filters')}
              </button>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {filteredMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  t={t}
                  locale={locale}
                  onEdit={openEdit}
                  onDelete={handleDeleteMatch}
                  equipmentById={equipmentById}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {formMode ? (
        <MatchForm
          mode={formMode}
          draft={draft}
          setDraft={setDraft}
          equipmentList={equipmentList}
          onClose={closeForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          t={t}
        />
      ) : null}
    </div>
  );
};

export default MatchesPage;
